import { useCallback, useEffect, useState } from 'react';
import { marketingApi, type SharedTemplate } from '@/api/marketing.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { showApiError } from '@/utils/apiError';
import { MarketingEmptyState } from './components/MarketingEmptyState';
import { notify } from '@/core/toast/notify';

export function MarketingTemplatesPage() {
  const [templates, setTemplates] = useState<SharedTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await marketingApi.listTemplates());
    } catch (err) {
      showApiError(err, 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim()) {
      notify.error('Template name is required');
      return;
    }
    setSaving(true);
    try {
      await marketingApi.createTemplate({
        templateName: name.trim(),
        templateCode: name.trim().toLowerCase().replace(/\s+/g, '_'),
        channel: 'EMAIL',
        subject,
        bodyText: body,
      } as SharedTemplate & { templateCode: string });
      notify.success('Template saved');
      setName('');
      setSubject('');
      setBody('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates"
        description="Reusable email content with versioning via the shared template service."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Marketing', to: '/marketing' },
          { label: 'Templates' },
        ]}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="card p-5 space-y-3">
          <h2 className="text-sm font-semibold text-slate-800">New email template</h2>
          <Input
            placeholder="Template name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            placeholder="Subject line"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <Textarea
            className="h-40 font-mono text-xs"
            placeholder="Body (HTML or plain text)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button loading={saving} onClick={() => void create()}>
            Save template
          </Button>
        </section>

        <section className="card">
          <h2 className="p-4 text-sm font-semibold border-b border-slate-100">Library</h2>
          {loading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : templates.length === 0 ? (
            <MarketingEmptyState title="No templates" description="Create a template to attach to email campaigns." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {templates.map((t) => (
                <li key={t.id} className="p-4 text-sm">
                  <p className="font-medium text-slate-800">{t.templateName}</p>
                  <p className="text-slate-500 text-xs mt-0.5">
                    {t.channel} · v{t.version ?? 1}
                    {t.subject ? ` · ${t.subject}` : ''}
                    {t.published ? ' · published' : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

